import {
  fetch_all_main_sections,
  fetch_all_sections_catalog,
  fetch_main_sections_by_form,
} from '@/services/main_section_service';
import {
  buildFallbackQuestion,
  buildNormalizedMainSection,
  buildNormalizedQuestion,
  buildNormalizedSection,
  flattenMainSections,
  getDependentQuestionFlags,
  getQuestionIdentity,
  getQuestionOptionInputs,
  getQuestionTypeValue,
  joinMessageItems,
  normalizeChoiceLabel,
  parseNormalizedErrorMessage,
} from './create_sections_normalization_helpers.js';

export const create_sections_normalization_methods = {
  normalize_error_message(message, fallback = 'Something went wrong.') {
    if (Array.isArray(message)) {
      const joined_message = joinMessageItems(message, fallback);
      return joined_message;
    }
    if (typeof message !== 'string') {
      return fallback;
    }

    const trimmed_message = message.trim();
    if (!trimmed_message) {
      return fallback;
    }

    const normalized_message = parseNormalizedErrorMessage(trimmed_message, fallback);
    return normalized_message;
  },

  format_question_type_label(value) {
    const formatted_label = String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => {
      const uppercase_letter = letter.toUpperCase();
      return uppercase_letter;
    }).trim();
    return formatted_label;
  },

  is_dependent_question(question = {}) {
    const dependent_flags = getDependentQuestionFlags(question);
    const is_dependent = Boolean(dependent_flags.find(Boolean));
    return is_dependent;
  },

  build_question_form(order_number = 1) {
    const question_form = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      question_text: '',
      question_description: '',
      question_type_id: '',
      is_mandatory: true,
      is_unique: false,
      question_number: order_number,
      multi_select_options: [],
      trigger_condition: null,
      dependant_questions: [],
      show_option_inputs: false,
      option_inputs: ['', ''],
    };
    return question_form;
  },

  normalize_question(question, index = 0) {
    if (!question || typeof question !== 'object') {
      const fallback_question = buildFallbackQuestion(index, question);
      return fallback_question;
    }

    const question_type_value = getQuestionTypeValue(question);
    const option_inputs = getQuestionOptionInputs(this, question);
    const question_id = getQuestionIdentity(question);
    const normalized_question = buildNormalizedQuestion(
      this,
      question,
      index,
      question_id,
      question_type_value,
      option_inputs
    );
    return normalized_question;
  },

  normalize_section(item, index = 0) {
    const normalized_section = buildNormalizedSection(this, item, index);
    return normalized_section;
  },

  normalize_main_section(item, index = 0) {
    const normalized_main_section = buildNormalizedMainSection(this, item, index);
    return normalized_main_section;
  },

  async load_main_sections() {
    const form_id = Number(this.topBar.formId);
    if (!form_id) {
      this.main_sections = [];
      this.sections = [];
      return;
    }

    const items = await fetch_main_sections_by_form(form_id);
    const normalized_main_sections = items.map((item, current_index) => {
      const normalized_main_section = this.normalize_main_section(item, current_index);
      return normalized_main_section;
    });
    normalized_main_sections.sort((left, right) => {
      const order_difference = Number(left.order_number) - Number(right.order_number);
      return order_difference;
    });

    this.main_sections = normalized_main_sections;
    this.sections = flattenMainSections(this.main_sections);
  },

  async load_form_sections() {
    await this.load_main_sections();
  },

  async load_reusable_section_catalogs() {
    const [main_sections, sections] = await Promise.all([
      fetch_all_main_sections().catch(() => { return []; }),
      fetch_all_sections_catalog().catch(() => { return []; }),
    ]);
    this.reusable_main_sections = Array.isArray(main_sections) ? main_sections : [];
    this.reusable_sub_sections = Array.isArray(sections) ? sections : [];
  },

  resolve_main_section_name() {
    const main_section_name = this.main_section_mode === 'reuse'
      ? String(this.selected_reusable_main_section?.name || '').trim()
      : String(this.new_main_section.main_section_name || '').trim();
    return main_section_name;
  },

  resolve_sub_section_name() {
    const sub_section_name = this.sub_section_mode === 'reuse'
      ? String(this.selected_reusable_sub_section?.name || '').trim()
      : String(this.new_sub_section.section_name || '').trim();
    return sub_section_name;
  },

  extract_choice_label(choice) {
    const choice_label = normalizeChoiceLabel(choice);
    return choice_label;
  },

  question_type_requires_options(question_type) {
    const normalized_type = String(question_type || '').toLowerCase();
    const requires_options = normalized_type.includes('single_select') || normalized_type.includes('multi_select');
    return requires_options;
  },
};
